import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../services/api";
import { Check, X, ShieldAlert, Shield, Users, Trash2 } from "lucide-react";

const STATUS_STYLE = {
    pending: { bg: "#fff5e3", fg: "#8a560f", label: "Pending" },
    active: { bg: "#e9f5ec", fg: "#1a5a2f", label: "Active" },
    suspended: { bg: "#eeeeee", fg: "#4a4a4a", label: "Suspended" },
};

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/auth/users");
            setUsers(data);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/auth/users/${id}`, { status });
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const setRole = async (id, role) => {
        try {
            await api.patch(`/auth/users/${id}`, { role });
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Delete user ${u.email}?\n\nThis will also remove ALL their RFQs and quotations. This action cannot be undone.`)) return;
        try {
            await api.delete(`/auth/users/${u.id}`);
            await load();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const pending = users.filter((u) => u.status === "pending");
    const others = users.filter((u) => u.status !== "pending");

    return (
        <div data-testid="admin-users-page">
            <div className="mb-10">
                <div className="font-body text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: "var(--ns-muted)" }}>
                    Access management
                </div>
                <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight" style={{ color: "var(--ns-ink)" }}>
                    Users
                </h1>
            </div>

            {err && (
                <div className="mb-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm" style={{ color: "#7a2218" }}>
                    {err}
                </div>
            )}

            {pending.length > 0 && (
                <section className="bg-white border border-[var(--ns-border)] mb-8">
                    <div className="px-6 lg:px-8 py-5 border-b border-[var(--ns-border)] flex items-center justify-between">
                        <div>
                            <h2 className="font-display text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--ns-ink)" }}>
                                Pending approval
                            </h2>
                            <p className="font-body text-xs mt-1" style={{ color: "var(--ns-muted)" }}>
                                New registration requests awaiting validation.
                            </p>
                        </div>
                        <span
                            className="inline-flex items-center gap-2 px-3 py-1.5 font-body text-[11px] uppercase tracking-[0.14em] font-semibold"
                            style={{ background: "#fff5e3", color: "#8a560f" }}
                        >
                            {pending.length} pending
                        </span>
                    </div>
                    <UserTable users={pending} pending onApprove={(id) => setStatus(id, "active")} onReject={(id) => setStatus(id, "suspended")} onDelete={removeUser} />
                </section>
            )}

            <section className="bg-white border border-[var(--ns-border)]">
                <div className="px-6 lg:px-8 py-5 border-b border-[var(--ns-border)] flex items-center gap-3">
                    <Users size={16} style={{ color: "var(--ns-muted)" }} />
                    <h2 className="font-display text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--ns-ink)" }}>
                        All users
                    </h2>
                </div>
                {loading ? (
                    <div className="px-6 py-12 text-center font-body text-sm" style={{ color: "var(--ns-muted)" }}>Loading…</div>
                ) : (
                    <UserTable users={others} onSuspend={(id) => setStatus(id, "suspended")} onReinstate={(id) => setStatus(id, "active")} onRole={setRole} onDelete={removeUser} />
                )}
            </section>
        </div>
    );
};

const UserTable = ({ users, pending = false, onApprove, onReject, onSuspend, onReinstate, onRole, onDelete }) => (
    <div className="overflow-x-auto">
        <table className="w-full">
            <thead>
                <tr style={{ background: "#fafaf8" }} className="border-b border-[var(--ns-border)]">
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Company</Th>
                    <Th>Role</Th>
                    <Th>Status</Th>
                    <Th>Joined</Th>
                    <Th align="right">Actions</Th>
                </tr>
            </thead>
            <tbody>
                {users.length === 0 ? (
                    <tr><td colSpan={7} className="px-6 py-12 text-center font-body text-sm" style={{ color: "var(--ns-muted)" }}>No users.</td></tr>
                ) : users.map((u) => {
                    const s = STATUS_STYLE[u.status] || STATUS_STYLE.pending;
                    return (
                        <tr key={u.id} className="border-b border-[var(--ns-border)] last:border-0 hover:bg-[#fbfaf7]" data-testid={`user-row-${u.email}`}>
                            <Td><span className="font-display font-semibold" style={{ color: "var(--ns-ink)" }}>{u.name || "—"}</span></Td>
                            <Td><span className="break-all">{u.email}</span></Td>
                            <Td>{u.company || "—"}</Td>
                            <Td>
                                <span className="inline-flex items-center gap-1.5 font-body text-xs uppercase tracking-[0.14em]" style={{ color: "var(--ns-body)" }}>
                                    {u.role === "admin" ? <ShieldAlert size={13} /> : <Shield size={13} />}
                                    {u.role}
                                </span>
                            </Td>
                            <Td>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 font-body text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ background: s.bg, color: s.fg }}>
                                    {s.label}
                                </span>
                            </Td>
                            <Td>
                                <span className="font-body text-xs tabular-nums" style={{ color: "var(--ns-muted)" }}>{(u.created_at || "").slice(0, 10)}</span>
                            </Td>
                            <Td align="right">
                                <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                    {pending ? (
                                        <>
                                            <ActionBtn onClick={() => onApprove(u.id)} variant="primary" testid={`approve-${u.email}`}>
                                                <Check size={13} /> Approve
                                            </ActionBtn>
                                            <ActionBtn onClick={() => onReject(u.id)} testid={`reject-${u.email}`}>
                                                <X size={13} /> Reject
                                            </ActionBtn>
                                        </>
                                    ) : (
                                        <>
                                            {u.role !== "admin" && (
                                                u.status === "active" ? (
                                                    <ActionBtn onClick={() => onSuspend(u.id)} testid={`suspend-${u.email}`}>Suspend</ActionBtn>
                                                ) : (
                                                    <ActionBtn onClick={() => onReinstate(u.id)} variant="primary" testid={`reinstate-${u.email}`}>Reinstate</ActionBtn>
                                                )
                                            )}
                                            {u.role !== "admin" && (
                                                <ActionBtn onClick={() => onRole(u.id, "admin")} testid={`promote-${u.email}`}>Make admin</ActionBtn>
                                            )}
                                            {u.role !== "admin" && (
                                                <ActionBtn onClick={() => onDelete(u)} testid={`delete-${u.email}`}>
                                                    <Trash2 size={12} /> Delete
                                                </ActionBtn>
                                            )}
                                        </>
                                    )}
                                </div>
                            </Td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const Th = ({ children, align = "left" }) => (
    <th className={`px-4 py-3.5 font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-${align}`} style={{ color: "var(--ns-muted)" }}>
        {children}
    </th>
);
const Td = ({ children, align = "left" }) => (
    <td className={`px-4 py-3 font-body text-sm text-${align}`} style={{ color: "var(--ns-body)" }}>{children}</td>
);

const ActionBtn = ({ children, onClick, variant, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 font-body text-[11px] uppercase tracking-[0.14em] border transition-colors ${
            variant === "primary"
                ? "bg-[var(--ns-ink)] text-white border-[var(--ns-ink)]"
                : "bg-white border-[var(--ns-border)] hover:border-[var(--ns-ink)]"
        }`}
        style={variant !== "primary" ? { color: "var(--ns-body)" } : {}}
    >
        {children}
    </button>
);

export default AdminUsers;
